<template>
  <v-card flat class="external-store-sync">
    <v-card-title>Tienda en línea</v-card-title>
    <v-card-text>
      <v-alert v-if="loadError" type="warning" dense outlined>{{ loadError }}</v-alert>
      <v-form ref="form" @submit.prevent="saveConfiguration">
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field v-model="form.apiBaseUrl" label="URL base del API" placeholder="Ej. https://api.ejemplo.com/v1"
              :rules="urlRules" autocomplete="off" outlined dense />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field v-model="form.apiKey" :label="apiKeyLabel" :rules="apiKeyRules" type="password"
              autocomplete="new-password" outlined dense />
          </v-col>
          <v-col cols="12" md="6">
            <v-autocomplete v-model="form.idWarehouse" :items="warehouses" item-text="Warehouse" item-value="ID_Warehouse"
              label="Bodega que publica la tienda" :rules="warehouseRules" :loading="loadingWarehouses"
              no-data-text="No hay bodegas disponibles" outlined dense />
          </v-col>
          <v-col cols="12" md="6">
            <v-autocomplete v-model="form.priceListName" :items="priceLists" label="Lista de precios del catálogo"
              :rules="priceListRules" :loading="loadingPriceLists" no-data-text="No hay listas de precios" outlined dense />
          </v-col>
          <v-col cols="12" class="d-flex flex-wrap pt-0 pb-6">
            <v-switch v-model="form.allowLots" :label="form.allowLots ? 'Permitir existencias con lotes' : 'Solo existencias sin lote'"
              class="mt-0 mr-8" inset dense hide-details />
            <v-switch v-model="form.isActive" label="Sincronización activa" class="mt-0" inset dense hide-details />
          </v-col>
        </v-row>
        <v-btn type="submit" color="primary" :loading="saving" :disabled="testing || syncingAll">Guardar configuración</v-btn>
        <v-btn class="ml-2" color="secondary" :loading="testing" :disabled="!configuration || saving || syncingAll || hasUnsavedChanges"
          @click="testConnection">
          Probar conexión
        </v-btn>
        <div v-if="configuration && hasUnsavedChanges" class="caption mt-2">Guarde los cambios antes de probar la conexión.</div>
        <v-alert v-if="pingResult" class="mt-4" :type="pingResult.ok ? 'success' : 'error'" dense outlined>
          {{ pingResult.message }}
        </v-alert>
        <v-divider class="my-6" />
        <v-btn color="primary" outlined :loading="syncingAll" :disabled="!canSyncAll" @click="syncAllProducts">
          Sincronizar todos los productos
        </v-btn>
        <div v-if="configuration" class="caption mt-2">
          <template v-if="configuration.isActive">
            Envía a la tienda todos los productos de la lista «{{ configuration.priceListName }}» en lotes de 500.
            Úselo una vez al inicio; puede tardar varios minutos.
          </template>
          <template v-else>Active la sincronización y guarde para enviar todos los productos.</template>
        </div>
        <v-alert v-if="syncAllResult" class="mt-4" :type="syncAllResult.type" dense outlined>
          {{ syncAllResult.message }}
        </v-alert>
      </v-form>
    </v-card-text>
  </v-card>
</template>

<script>
const emptyForm = () => ({ apiBaseUrl: '', apiKey: '', idWarehouse: null, priceListName: null, allowLots: false, isActive: true });
// Sin espacios (una URL pegada de un PDF trae uno donde se partió la línea) ni barras finales.
const normalizeUrl = (value) => (value || '').replace(/\s+/g, '').replace(/\/+$/, '');
const SYNC_ALL_POLL_MS = 5000;
const SYNC_ALL_RUNNING = 'La sincronización de todos los productos sigue en el servidor. El resumen aparecerá aquí al terminar.';
const SYNC_ALL_STATUS_RETRIES = 3;

export default {
  name: 'ManageExternalStoreSync',
  data() {
    return {
      configuration: null,
      loadError: '',
      form: emptyForm(),
      warehouses: [],
      loadingWarehouses: false,
      priceLists: [],
      loadingPriceLists: false,
      saving: false,
      testing: false,
      pingResult: null,
      syncingAll: false,
      syncAllResult: null,
      syncAllTimer: null,
      // Validación rápida; el DTO del backend es la fuente de verdad.
      urlRules: [
        (v) => !!normalizeUrl(v) || 'La URL es obligatoria',
        (v) => /^https:\/\/[^?#@]+$/.test(normalizeUrl(v)) || 'Use https:// sin ?, # ni usuario/contraseña',
        (v) => normalizeUrl(v).length <= 500 || 'Máximo 500 caracteres'
      ],
      apiKeyRules: [(v) => (v || '').trim().length <= 500 || 'Máximo 500 caracteres'],
      warehouseRules: [(v) => !!v || 'Seleccione una bodega'],
      priceListRules: [(v) => !!v || 'Seleccione una lista de precios']
    };
  },
  computed: {
    apiKeyLabel() {
      return this.configuration && this.configuration.apiKeyConfigured
        ? 'Nueva API key (vacío = conservar; obligatoria si cambia la URL)'
        : 'API key';
    },
    // El ping usa la configuración guardada: no se prueba lo que aún no se guardó.
    hasUnsavedChanges() {
      const saved = this.configuration;
      if (!saved) return true;
      return normalizeUrl(this.form.apiBaseUrl) !== saved.apiBaseUrl
        || this.form.idWarehouse !== saved.idWarehouse
        || this.form.priceListName !== saved.priceListName
        || this.form.allowLots !== saved.allowLots
        || this.form.isActive !== saved.isActive
        || this.form.apiKey.trim() !== '';
    },
    // Envía con la configuración guardada y activa: nunca con lo que aún no se guardó.
    canSyncAll() {
      return Boolean(this.configuration && this.configuration.isActive)
        && !this.hasUnsavedChanges && !this.saving && !this.testing;
    }
  },
  mounted() {
    this.loadWarehouses();
    this.loadPriceLists();
    this.loadConfiguration();
  },
  beforeDestroy() {
    clearTimeout(this.syncAllTimer);
  },
  methods: {
    errorMessage(error, fallback) {
      return error?.response?.data?.message || error?.message || fallback;
    },
    async loadWarehouses() {
      this.loadingWarehouses = true;
      try {
        const response = await this.$API.warehouse.getWarehousesByBusiness({ notShowReserve: 1 });
        this.warehouses = Array.isArray(response) ? response : [];
      } catch (error) {
        this.$toasted.error('No se pudieron cargar las bodegas');
      } finally {
        this.loadingWarehouses = false;
      }
    },
    // Las mismas listas que el selector del POS para administradores.
    async loadPriceLists() {
      this.loadingPriceLists = true;
      try {
        const response = await this.$API.priceList.priceListNoData();
        this.priceLists = [...new Set((Array.isArray(response) ? response : []).map((list) => list.id))];
      } catch (error) {
        this.$toasted.error('No se pudieron cargar las listas de precios');
      } finally {
        this.loadingPriceLists = false;
      }
    },
    // Nunca se precarga la API key: el backend solo informa apiKeyConfigured.
    applyConfiguration(configuration) {
      this.configuration = configuration || null;
      this.form = emptyForm();
      if (this.configuration) {
        this.form.apiBaseUrl = this.configuration.apiBaseUrl || '';
        this.form.idWarehouse = this.configuration.idWarehouse || null;
        this.form.priceListName = this.configuration.priceListName || null;
        this.form.allowLots = this.configuration.allowLots === true;
        this.form.isActive = this.configuration.isActive === true;
      }
    },
    async loadConfiguration() {
      try {
        const response = await this.$API.externalStoreSync.getConfiguration();
        this.applyConfiguration(response.data);
        this.loadError = '';
        if (this.configuration) this.checkSyncAll(); // pudo quedar corriendo (recarga u otra pestaña)
      } catch (error) {
        // Una consulta fallida no debe impedir crear la configuración desde esta pantalla.
        // El backend valida nuevamente el acceso y los datos al guardar.
        this.loadError = this.errorMessage(error, 'No se pudo cargar la configuración');
      }
    },
    async saveConfiguration() {
      if (this.saving || !this.$refs.form.validate()) return;
      const apiKey = this.form.apiKey.trim();
      const apiBaseUrl = normalizeUrl(this.form.apiBaseUrl);
      // Misma regla que el backend: la key es obligatoria al crear o si cambia la URL.
      if (!apiKey && (!this.configuration || apiBaseUrl !== this.configuration.apiBaseUrl)) {
        this.$toasted.error('Ingrese la API key (obligatoria al crear o si cambia la URL)');
        return;
      }
      this.saving = true;
      try {
        const response = await this.$API.externalStoreSync.saveConfiguration({
          apiBaseUrl,
          apiKey: apiKey || null,
          idWarehouse: this.form.idWarehouse,
          priceListName: this.form.priceListName,
          allowLots: this.form.allowLots,
          isActive: this.form.isActive
        });
        this.applyConfiguration(response.data);
        this.pingResult = null;
        this.syncAllResult = null;
        this.$toasted.success(response.message || 'Configuración guardada');
      } catch (error) {
        this.$toasted.error(this.errorMessage(error, 'No se pudo guardar la configuración'));
      } finally {
        this.saving = false;
      }
    },
    async testConnection() {
      this.testing = true;
      this.pingResult = null;
      try {
        const response = await this.$API.externalStoreSync.ping();
        this.pingResult = { ok: response.success === true, message: response.message || 'La prueba terminó' };
      } catch (error) {
        this.pingResult = { ok: false, message: this.errorMessage(error, 'No se pudo probar la conexión') };
      } finally {
        this.testing = false;
      }
    },
    // Una vez al inicio: el servidor revisa la lista y la tienda, responde al momento y envía los lotes en segundo plano.
    async syncAllProducts() {
      if (this.syncingAll || !this.canSyncAll) return;
      this.syncingAll = true;
      this.syncAllResult = null;
      try {
        const response = await this.$API.externalStoreSync.syncAllProducts();
        if (response.success === true) { // 202: los lotes siguen en el servidor; queda en curso hasta que termine
          this.syncAllResult = { type: 'info', message: response.message || SYNC_ALL_RUNNING };
          this.watchSyncAll();
          return;
        }
        this.syncAllResult = { type: 'warning', message: response.message || 'No se pudo iniciar la sincronización' };
      } catch (error) {
        const status = error?.response?.status;
        // Sin respuesta o corte del proxy: el servidor puede seguir enviando lotes.
        const message = !error?.response || status >= 502
          ? 'La sincronización puede seguir en el servidor. Espere unos minutos antes de repetirla.'
          : this.errorMessage(error, 'No se pudieron sincronizar los productos');
        this.syncAllResult = { type: status === 409 ? 'warning' : 'error', message };
      }
      this.syncingAll = false;
      this.checkSyncAll(); // pudo quedar corriendo (corte de red u otra pestaña): si sigue, se muestra y se espera
    },
    watchSyncAll(failures = 0) {
      clearTimeout(this.syncAllTimer);
      this.syncAllTimer = setTimeout(() => this.checkSyncAll(true, failures), SYNC_ALL_POLL_MS);
    },
    // Estado en el servidor. Al abrir la pestaña (waiting = false) solo importa si sigue corriendo;
    // mientras se espera, cada 5 s hasta que termine y entonces se muestra el resumen.
    async checkSyncAll(waiting = false, failures = 0) {
      let status = null;
      try {
        status = (await this.$API.externalStoreSync.syncAllProductsStatus()).data;
      } catch (error) {
        // Se maneja abajo: sin estado.
      }
      if (this._isDestroyed) return;
      if (!status) {
        if (!waiting) return;
        // Corte momentáneo: se reintenta; tras 3 fallos seguidos se deja de consultar y se liberan los botones.
        if (failures + 1 < SYNC_ALL_STATUS_RETRIES) {
          this.watchSyncAll(failures + 1);
          return;
        }
        this.syncingAll = false;
        this.syncAllResult = { type: 'warning', message: 'No se pudo consultar la sincronización; puede seguir en el servidor. Vuelva a abrir esta pestaña en unos minutos.' };
        return;
      }
      if (status.running) {
        this.syncingAll = true;
        if (this.syncAllResult?.type !== 'info') this.syncAllResult = { type: 'info', message: SYNC_ALL_RUNNING };
        this.watchSyncAll();
        return;
      }
      if (!waiting) return;
      const last = status.last;
      this.syncingAll = false;
      this.syncAllResult = last
        ? { type: last.status === 'CONFIRMED' ? 'success' : 'warning', message: last.message }
        : { type: 'warning', message: 'La sincronización ya no está en curso y no dejó resumen (no llegó a iniciar o el servidor se reinició). Puede volver a sincronizar.' };
    }
  }
};
</script>

<style scoped>
.external-store-sync {
  background-color: #ffffff !important;
}
</style>
